import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DataBlockInfo,
  defaultUnsavedChangesState,
  DocumentTemplateInfo,
  enabledFormTypes,
  RemoveDataBlock,
  UnsavedChangesState,
  UpdateFormState,
  UpdateFormValues,
  UpdateShowDetails,
} from './types';
import { Form } from '../../components/global-forms/GlobalForms.types';
import superjson from 'superjson';

export const unsavedChangesSlice = createSlice({
  name: 'unsavedChanges',
  initialState: defaultUnsavedChangesState,
  reducers: {
    globalFormOpened: (
      state,
      action: PayloadAction<Form>,
    ): UnsavedChangesState => {
      if (!enabledFormTypes.includes(action.payload.type)) {
        return state;
      }
      state.globalForms = [
        ...state.globalForms,
        {
          id: action.payload.id,
          form: {
            ...action.payload,
            formDirtyStateRetriever: undefined,
            resolve: undefined,
          },
        },
      ];
      return state;
    },
    globalFormStateChanged: (
      state,
      action: PayloadAction<UpdateFormState>,
    ): UnsavedChangesState => {
      state.globalForms = state.globalForms.map((globalForm) =>
        action.payload.id === globalForm.id
          ? {
              ...globalForm,
              form: { ...globalForm.form, state: action.payload.state },
            }
          : globalForm,
      );
      return state;
    },
    globalFormValuesChanged: (
      state,
      action: PayloadAction<UpdateFormValues>,
    ): UnsavedChangesState => {
      state.globalForms = state.globalForms.map((form) =>
        action.payload.id === form.id
          ? {
              ...form,
              values: superjson.serialize(action.payload.values),
              showDetails: action.payload.showDetails,
            }
          : form,
      );
      return state;
    },
    globalFormValuesRemoved: (
      state,
      action: PayloadAction<number>,
    ): UnsavedChangesState => {
      state.globalForms = state.globalForms.map((form) =>
        action.payload === form.id ? { ...form, values: undefined } : form,
      );
      return state;
    },
    globalFormClosed: (
      state,
      action: PayloadAction<number>,
    ): UnsavedChangesState => {
      state.globalForms = state.globalForms.filter(
        (form) => form.id !== action.payload,
      );
      return state;
    },
    globalFormShowDetailsChanged: (
      state,
      action: PayloadAction<UpdateShowDetails>,
    ): UnsavedChangesState => {
      state.globalForms = state.globalForms.map((globalForm) =>
        action.payload.id === globalForm.id
          ? {
              ...globalForm,
              showDetails: action.payload.showDetails,
            }
          : globalForm,
      );
      return state;
    },
    dataBlockCreated: (
      state,
      action: PayloadAction<DataBlockInfo>,
    ): UnsavedChangesState => {
      state.dataBlocks = [...state.dataBlocks, action.payload];
      return state;
    },
    dataBlockRemoved: (
      state,
      action: PayloadAction<RemoveDataBlock>,
    ): UnsavedChangesState => {
      state.dataBlocks = state.dataBlocks.filter(
        (dataBlock) =>
          dataBlock.tabId !== action.payload.tabId ||
          dataBlock.type !== action.payload.type ||
          dataBlock.id !== action.payload.id,
      );
      return state;
    },
    documentTemplateCreated: (
      state,
      action: PayloadAction<DocumentTemplateInfo>,
    ): UnsavedChangesState => {
      state.documentTemplates = [
        ...(state.documentTemplates ?? []),
        action.payload,
      ];
      return state;
    },
    documentTemplateRemoved: (
      state,
      action: PayloadAction<string>,
    ): UnsavedChangesState => {
      state.documentTemplates = (state.documentTemplates ?? []).filter(
        (documentTemplate) => documentTemplate.tabId !== action.payload,
      );
      return state;
    },
  },
});

export const UnsavedChangesActions = unsavedChangesSlice.actions;
